// productService.ts - Compatible with existing components
import axios from "axios";
import { API_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import toast from "react-hot-toast";
import logger from "../utils/logger";

const API_ENDPOINT = API_URL + 'api/v1';

// Basic Product interface that can be extended by other components
export interface ProductImage {
  bucketUrl?: any;
  filePath?: string;
  fileName?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  fileSize?: string;
}

// Using a more flexible interface with optional properties
export interface Product {
  _id: string;
  name: string;
  description?: string | null;
  images?: ProductImage[];
  currency?: string;
  price: number;
  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    gst?: string;
    [key: string]: any;
  };
  isActive?: boolean;
  createdAt?: string;
  
  // Add other properties that might be used in different components
  // Using optional properties for flexibility
  quantity?: number;
  invoiceid?: string;
  quotationid?: string;
  unit?: string;
  [key: string]: any; // Allow any additional properties
}

// Define request parameter types more flexibly
export interface ProductListParams {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  orderBy?: string; // Add this to be compatible with existing code
  isAscending?: boolean; // Add this to be compatible with existing code
  [key: string]: any; // Allow any additional properties
}

// API response type
export interface ApiResponse<T> {
  data: T;
  total: number;
  success?: boolean;
  message?: string;
}

// Get all products with flexible typing
export const getAllProducts = (params: ProductListParams) => {
    let obj = {
        ...getHeaderOptions,
        Authorization: localStorage.getItem('auth_token') || ''
    }
    return axios.get(`${API_ENDPOINT}/product`, { headers: obj, params: params });
}

export const getProductDetails = (id: string) => {
    let obj = {
        ...getHeaderOptions,
        Authorization: localStorage.getItem('auth_token') || ''
    }
    return axios.get(`${API_ENDPOINT}/product/${id}`, { headers: obj });
}

// Create new product with flexible typing
export const createNewProduct = (data: {
  name: string;
  description?: string;
  price: string | number;
  category?: string;
  currency?: string;
  extraDetails?: any;
  [key: string]: any; // Allow any additional properties
}) => {
    logger.log('Creating product with data:', data);
    let obj = {
        ...getHeaderOptions,
        Authorization: localStorage.getItem('auth_token') || ''
    }
    return axios.post(`${API_ENDPOINT}/product`, data, { headers: obj });
}

// Update product with flexible typing
export const updateProduct = (id: string, data: {
  name?: string;
  category?: string;
  description?: string;
  price?: string | number;
  currency?: string;
  extraDetails?: any;
  [key: string]: any; // Allow any additional properties
}) => {
    logger.log('Updating product with ID:', id, 'and data:', data);
    let obj = {
        ...getHeaderOptions,
        Authorization: localStorage.getItem('auth_token') || ''
    }
    return axios.put(`${API_ENDPOINT}/product/${id}`, data, { headers: obj });
}

export const deleteProduct = (id: string) => {
    let obj = {
        ...getHeaderOptions,
        Authorization: localStorage.getItem('auth_token') || ''
    }
    return axios.delete(`${API_ENDPOINT}/product/${id}`, { headers: obj });
}

/**
 * Delete a product image from the server
 * @param productId The ID of the product
 * @param filePath The file path of the image to delete (without bucket URL)
 * @returns Promise with the API response
 */
export const deleteProductImage = async (productId: string, filePath: string): Promise<any> => {
  if (!productId || !filePath) {
    throw new Error("Product ID and file path are required");
  }

  const payload = {
    type: "product",
    product: productId,
    filePaths: [filePath] // API expects an array of file paths
  };

  logger.log('Deleting product image:', payload);
  
  try {
    const response = await axios.put(
      `${API_ENDPOINT}/utility/delete-file`,
      payload,
      {
        headers: {
          ...getHeaderOptions,
          Authorization: localStorage.getItem("auth_token") || "",
        }
      }
    );
    return response;
  } catch (error) {
    console.error('Error deleting product image:', error);
    throw error;
  }
};

/**
 * Bulk import products from CSV file
 * @param formData FormData containing CSV file and field mappings
 * @returns Promise with import results
 */
export const bulkImportProducts = (formData: FormData) => {
  logger.log('Starting bulk import of products');
  
  const headers = {
      ...getHeaderOptions,
      Authorization: localStorage.getItem('auth_token') || '',
      // Don't set Content-Type here - axios will set the correct multipart/form-data with boundary
  };
  
  return axios.post(
      `${API_ENDPOINT}/product/bulk-add`, 
      formData, 
      { 
          headers,
          onUploadProgress: (progressEvent) => {
              // You can use this to track upload progress if needed
              if (progressEvent.total) {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  logger.log(`Upload progress: ${percentCompleted}%`);
              }
          }
      }
  );
};


/**
 * Generate a shareable product catalogue link
 * @param productIds Array of product IDs to include in the catalogue
 * @param leadInfo Optional lead information to associate with the shared catalogue
 * @returns Promise with the shareable link response
 */
export const generateShareableLink = async (
  productIds: string[], 
  leadInfo?: { leadId: string; leadName?: string }
): Promise<any> => {
  logger.log('Generating shareable catalogue link for products:', productIds);
  logger.log('Lead info for catalogue:', leadInfo);
  
  const payload = {
    products: productIds,
    ...(leadInfo && { lead: leadInfo.leadId })
  };
  
  const obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || ""
  };

  try {
    const response = await axios.post(
      `${API_ENDPOINT}/product/share-link`,
      payload,
      { headers: obj }
    );
    logger.log('Share link generated successfully:', response.data);
    return response;
  } catch (error) {
    console.error('Error generating shareable link:', error);
    throw error;
  }
};

/**
 * Share catalogue link via different methods
 * @param link The shareable link to share
 * @param method The sharing method ('whatsapp', 'email', 'copy')
 * @param customMessage Optional custom message to include
 */
export const shareCatalogueLink = (link: string, method: 'whatsapp' | 'email' | 'copy', customMessage?: string): void => {
  const defaultMessage = "Hey, here is the link to our Catalogue";
  const message = customMessage || defaultMessage;
  
  logger.log(`Sharing catalogue via ${method} with message: ${message}`);
  
  switch (method) {
    case 'whatsapp':
      // WhatsApp sharing
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${message}: ${link}`)}`;
      window.open(whatsappUrl, '_blank');
      logger.log('Opened WhatsApp sharing');
      break;
      
    case 'email':
      // Email sharing
      const emailSubject = 'Product Catalogue';
      const emailBody = `${message}: ${link}`;
      const emailUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = emailUrl;
      logger.log('Opened email client for sharing');
      break;
      
    case 'copy':
      // Copy to clipboard with hot toast notification
      navigator.clipboard.writeText(`${message}: ${link}`)
        .then(() => {
          logger.log('Link copied to clipboard');
          // Use hot toast notification
          toast.success('Link copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy link:', err);
          toast.error('Failed to copy link');
        });
      break;
      
    default:
      console.error('Invalid sharing method');
  }
};